// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------

// React
import { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';

// Material UI
import {
  Box,
  Card,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
  Zoom,
} from "@mui/material";

// Icons
import FlipIcon from '@mui/icons-material/Flip';
import SettingsIcon from '@mui/icons-material/Settings';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

// Custom Components
import AligningBox from '../utils/AligningBox';
import { ActionButtonOverlay } from '../utils/ActionButtons.js';
import { SelectDeviceDialog } from '../mediaDevices/SelectDeviceDialog';
import IconToggleButton from '../utils/IconToggleButton';
import LoadingIcon from "../loading/LoadingIcon.js";
import RecordingIndicator from '../utils/RecordingIndicator';
import { RenderIf, RenderIfElse } from '../utils/RenderIf';
import VideoCanvas from '../mediaDevices/VideoCanvas';

// Custom Scripts
import { WebcamState } from '../../scripts/video/webcam';



// ----------------------------------------------------------------------------
//   Component: Video Feed
//   Renders the webcam footage to a canvas.
// ----------------------------------------------------------------------------

let contentType = null;

export function WebcamVideoFeed({ webcam, createVideo = true, showGuides = false }) {


  // --------------------------------------------------------------------------
  //   Context & State
  // --------------------------------------------------------------------------

  // Directly track if the webcam feed is connected
  // This forces the feed to reconnect on a React refresh
  const [ webcamConnected, setWebcamConnected ] = useState(false);


  // --------------------------------------------------------------------------
  //   Effects
  // --------------------------------------------------------------------------

  // Ensure webcam is started & connected to the video feed
  // Triggers when state is set to LOADING, as well as on refresh if state is
  // SUCCESS but the webcam isn't connected
  useEffect(() => {
    if (
      webcam.stateIs( WebcamState.LOADING )
      || ( webcam.stateIs( WebcamState.SUCCESS ) && !webcamConnected )
    ) {
      webcam.updateDevices({})
        .then(() => {
          webcam.setActive();
          setWebcamConnected(true);
        })
        .catch((err) => webcam.setFailure(err))
    }
  }, [ webcam, webcamConnected ])


  // Update the global content type to a supported type,
  // or throw an error if none of them are supported
  // contentType = getFirstSupportedType([ "video/mp4", "video/webm" ]);
  contentType = "video/mp4";


  // --------------------------------------------------------------------------
  //   Component Layout
  // --------------------------------------------------------------------------

  return (
    <Box width="100%" height="100%" backgroundColor="black">

      <RenderIf condition={ createVideo }>
        <video
          ref         = { webcam.getRef() }
          style       = {{"zIndex": "-999", "display": "none", "position": "absolute" }}
          autoPlay    = {true}
          muted       = {true}
          playsInline = {true}
          controls    = {true}
        ></video>
      </RenderIf>

      <VideoCanvas
        videoRef   = { webcam.getRef() }
        flipped    = { webcam.isFlipped() }
        showGuides = { showGuides }
      />

    </Box>
  )
}



// ----------------------------------------------------------------------------
//   Component: Webcam Video
//   Formatted webcam bideo object with status overlay
// ----------------------------------------------------------------------------

export function WebcamVideo({
  webcam,
  settingsLink      = "",
  useSettingsDialog = false,
  showSettingsIcon  = true,
  showOptionsBar    = false,
  showRecordingIcon = false,
  isRecording       = false,
  showGuides        = false,
  startText         = "Start Camera",
  showErrMsg        = true,
}) {

  // --------------------------------------------------------------------------
  //   Context & State
  // --------------------------------------------------------------------------

  // Set the height of the options menu
  const optionsBarHeight = '48px';

  // Calculate overlay height based on presence/absence of options menu
  const overlayHeight = showOptionsBar ? `calc(100% - ${optionsBarHeight})` : '100%';

  // Track settings menu
  const [ webcamDialogOpen, setWebcamDialogOpen ] = useState(false);

  // Custom styles for icon buttons
  const buttonStyles = {
    sx: {
      'color': "#4447ad",
      '&:disabled': {
        color: 'rgba(223, 223, 223)',
      },
    }
  }


  // --------------------------------------------------------------------------
  //   Callbacks & Helper Functions
  // --------------------------------------------------------------------------

  const openWebcamSettings = () => {
    setWebcamDialogOpen(true);
  };

  const closeWebcamSettings = () => {
    setWebcamDialogOpen(false);
  };


  // --------------------------------------------------------------------------
  //   Component Layout
  // --------------------------------------------------------------------------

  return (
    <Card h="100%" sx={{position: "relative"}}>

      {/* Primary content */}
      <Stack height="100%" direction="column">

        {/* Webcam video feed */}
        <Box flexGrow={1} position="relative" minHeight="0">
          <WebcamVideoFeed id="webcam-canvas"
            createVideo = { true }
            webcam      = { webcam }
            showGuides  = { showGuides }
          />
        </Box>

        {/* Options bar */}
        <RenderIf condition={ showOptionsBar }>
          <Stack direction="row" height={optionsBarHeight} gap="4px" paddingX="8px">

            {/* Recording indicator */}
            <RecordingIndicator active={ isRecording } showBg={false} />

            <Box flexGrow={1}></Box>

            {/* Flip Camera */}
            <IconToggleButton
              state={ webcam.isFlipped() } onToggle={ webcam.toggleFlipped }
              iconActive   = { <FlipIcon /> }
              {...buttonStyles}
            />

            {/* Blur Camera */}
            <IconToggleButton
              state={ webcam.isBlurred() } onToggle={ webcam.toggleBlurred }
              iconActive   = { <VisibilityOffIcon /> }
              iconInactive = { <VisibilityIcon /> }
              {...buttonStyles}
            />

            {/* Settings */}
            <RenderIf condition={showSettingsIcon}>
              <RenderIfElse condition={useSettingsDialog}>

                  {/* Case 1: Settings Dialog */}
                  <IconButton onClick={ openWebcamSettings } {...buttonStyles}>
                    <SettingsIcon />
                  </IconButton>

                  {/* Case 2: Settings Link */}
                  <IconButton component={ NavLink } to={ settingsLink } {...buttonStyles}>
                    <SettingsIcon />
                  </IconButton>

              </RenderIfElse>
            </RenderIf>

          </Stack>
        </RenderIf>
      </Stack>

      {/* Video Blur Effect */}
      <RenderIf condition={ webcam.isBlurred() }>
        <Box
          position="absolute" top={0} left={0}
          width="100%" height={ overlayHeight }
          sx={{ backdropFilter: 'blur(20px) grayscale(100%) brightness(40%)' }}
          className="noselect"
        >
          <AligningBox textAlign="center">
            <Stack direction="column" maxWidth="400px" gap={1}>
              {/* <Typography color="white" fontSize="large" fontWeight="bold">
                Please Note:
              </Typography> */}
              <Typography color="white" fontSize="large" maxWidth="400px" px={2}>
                Your unblurred camera feed will still be recorded.
              </Typography>
            </Stack>
          </AligningBox>
        </Box>
      </RenderIf>

      {/* Overlay */}
      <RenderIf condition={ !webcam.stateIs( WebcamState.SUCCESS ) }>

        {/* Loading and error overlays - separate bc of animations on action button */}
        <AligningBox top={0} left={0} height={ overlayHeight } sx={{ pointerEvents: "none" }}>
          {
            // If webcam is loading, show a progress indicator
            webcam.stateIs( WebcamState.LOADING )
              ? <LoadingIcon />
              :

            // If there was an error, display text on the screen
            (webcam.stateIs( WebcamState.FAILURE ) && showErrMsg)
              ? <Typography paddingX={6} textAlign="center" variant="body1" color="error.light" fontSize="1.2em">
                  There was a problem loading the camera.
                  <br />
                  { `${webcam.getError()}` }
                </Typography>

            // If none of the above apply, don't show anything
              : null
          }
        </AligningBox>

        {/* Start button overlay */}
        <AligningBox top={0} left={0} height={ overlayHeight }>
          <ActionButtonOverlay
            content = { startText }
            visible = { webcam.stateIs( WebcamState.NONE ) }
            onClick = { webcam.setLoading }
            endIcon = {<VideoCameraFrontIcon sx={{ fontSize: "2em !important", paddingBottom: "2px", paddingLeft: 1 }} />}
          />
        </AligningBox>
      </RenderIf>

      {/* Recording Indicator Overlay */}
      <RenderIf condition={ showRecordingIcon && !showOptionsBar }>
        <Box position="absolute" top="8px" left="8px" className="noselect">
          <RecordingIndicator active={ isRecording } />
        </Box>
      </RenderIf>

      {/* Device Settings Dialog */}
      <SelectDeviceDialog
        open           = { webcamDialogOpen }
        onClose        = { closeWebcamSettings }
        webcam         = { webcam }
      />

    </Card>
  )
}



// ----------------------------------------------------------------------------
//   Export(s)
// ----------------------------------------------------------------------------

export default { WebcamVideoFeed, WebcamVideo }
